import { FilterItem } from './filter';
import { Utils } from '../../../services/utils.service';
import { DateRange } from '@angular/material/datepicker';

export interface ICheckingStrategy {
	getKey(key: number): { checked: boolean, indeterminate: boolean };
	setKey(key: number, checked: boolean, indeterminate: boolean): void;
	any(key: number): boolean;
	clear(): void;
}

export interface ISelectingStrategy {
	get selected(): any;
	getSelected(): any | null;
	getInitial?(): any;
	getSelectedValues(): any;
	setSelected(item: FilterItem): void;
	setCustomSelected?(item: FilterItem): void;
	removeSelected(id: number): void;
	isSelected(): boolean;
	clear(): void;
}

export interface ITreeSelectingStrategy extends ISelectingStrategy {
	get selected(): Array<FilterItem>;
	getSelected(): Array<number>;
	getSelectedValues(): Array<FilterItem>;
}

export interface IBoolSelectingStrategy extends ISelectingStrategy {
	get selected(): number;
	getSelected(): boolean | null;
	getSelectedValues(): Array<{ id: number, value: string }>;
}

export interface IStrSelectingStrategy extends ISelectingStrategy {
	get selected(): string | number;
	getSelected(): string | number | null;
	getSelectedValues(): Array<{ id: string | number, value: string }>;
}

export interface IDateSelectingStrategy extends ISelectingStrategy {
	get selected(): { [key: string]: DateRange<Date> };
	getSelected(): { [key: string]: DateRange<Date> };
	getSelectedValues(): Array<DateRange<Date>>;
}

export interface IMultiTreeSelectingStrategy extends ISelectingStrategy {
	get selected(): Array<FilterItem>;
	getSelected(): Array<number>;
	getInitial(): Array<Array<number>>;
	getSelectedValues(): Array<FilterItem>;
}

export class TreeCheckingStrategy implements ICheckingStrategy {

	private _strategy: { [key: number]: { checked: boolean, indeterminate: boolean } } = {};

	constructor(keys: Array<number> = [], checked: Array<number> = []) {
		keys.forEach((key: number) => this._strategy[key] = { checked: false, indeterminate: false });
		checked.forEach((key: number) => this._strategy[key].checked = true);
	}

	getKey(key: number) {
		if (!this._strategy[key]) this._strategy[key] = { checked: false, indeterminate: false };
		return this._strategy[key];
	}

	setKey(key: number, checked: boolean, indeterminate: boolean) {
		if (!this._strategy[key]) {
			this._strategy[key] = { checked, indeterminate }
		} else {
			this._strategy[key].checked = checked;
			this._strategy[key].indeterminate = indeterminate;
		}
	}

	any(key: number) {
		if (!this._strategy[key]) this._strategy[key] = { checked: false, indeterminate: false };
		return this._strategy[key].checked || this._strategy[key].indeterminate;
	}

	clear() {
		for (let item in this._strategy) {
			this._strategy[item].checked = false;
			this._strategy[item].indeterminate = false;
		}
	}
}

export class TreeSelectingStrategy implements ITreeSelectingStrategy {

	private _selected: { [key: number]: FilterItem } = {};

	get selected() {
		return Object.values(this._selected);
	}

	constructor(selected: Array<FilterItem> = []) {
		selected.forEach((item: FilterItem) => this._selected[item.id] = { ...item });
	}

	getSelected(): Array<number> {
		return Object.values(this._selected).map((item: FilterItem) => item.id as number);
	}

	getSelectedValues(): Array<FilterItem> {
		return Object.values(this._selected).sort(Utils.sortBy('value'));
	}

	setSelected(item: FilterItem) {
		this._selected[item.id] = { ...item };
	}

	removeSelected(id: number) {
		delete this._selected[id];
	}

	isSelected(): boolean {
		return !!this.getSelected().length;
	}

	clear() {
		for (let item in this._selected) delete this._selected[item];
	}
}

export class BoolSelectingStrategy implements IBoolSelectingStrategy {

	private _selected: number;

	get selected() {
		return this._selected;
	}

	constructor(selected: number = -1) {
		this._selected = selected;
	}

	getSelected(): boolean {
		return this._selected == -1 ? null : Boolean(this._selected);
	}

	getSelectedValues(): Array<{ id: number, value: string }> {
		return [{ id: -1, value: '' }];
	}

	setSelected(item: FilterItem) {
		this._selected = item.id as number;
	}

	removeSelected(id: number) {}

	isSelected(): boolean {
		return this._selected != -1;
	}

	clear() {
		this._selected = -1;
	}
}

export class StrSelectingStrategy implements IStrSelectingStrategy {

	private _selected: string | number;

	get selected() {
		return this._selected;
	}

	constructor(selected: string | number = -1) {
		this._selected = selected;
	}

	getSelected(): string | number {
		return this._selected == -1 ? null : this._selected;
	}

	getSelectedValues(): Array<{ id: string | number, value: string }> {
		return [{ id: -1, value: '' }];
	}

	setSelected(item: FilterItem) {
		this._selected = item.id;
	}

	removeSelected(id: number) {}

	isSelected(): boolean {
		return this._selected != -1;
	}

	clear() {
		this._selected = -1;
	}
}

export class DateSelectingStrategy implements IDateSelectingStrategy {
	private _selected: { [key: string]: DateRange<Date> } = {};

	get selected() {
		return this._selected;
	}

	constructor(selected: Array<FilterItem> = []) {
		selected.forEach((item: FilterItem) => this._selected[item.id] = (item.value as DateRange<Date>));
	}

	getSelected(): { [key: string]: DateRange<Date> } {
		return this.selected;
	}

	getSelectedValues(): Array<DateRange<Date>> {
		return Object.values(this._selected);
	}

	setSelected(item: FilterItem) {
		this._selected[item.id] = (item.value as DateRange<Date>);
	}

	removeSelected(id: number) {}

	isSelected(): boolean {
		return true;
	}

	clear() {
		for (let item in this._selected) delete this._selected[item];
	}
}

export class MultiTreeSelectingStrategy implements IMultiTreeSelectingStrategy {

	private _initial: Array<Array<number>> = [];
	private _selected: { [key: number]: FilterItem } = {};

	get selected(): Array<FilterItem> {
		return Object.values(this._selected);
	}

	constructor(initial: Array<Array<number>>) {
		this._initial = Utils.deepCopy(initial);
	}

	getInitial(): Array<Array<number>> {
		return this._initial;
	}

	getSelected(): Array<number> {
		return Object.values(this._selected).map((item: FilterItem) => item.id as number);
	}

	getSelectedValues(): Array<FilterItem> {
		return Object.values(this._selected).sort(Utils.sortBy('value'));
	}

	setCustomSelected(item: FilterItem): void {
		this._selected[item.id] = { ...item };
		this._initial.forEach((array: Array<number>) => array.push(item.id as number));
	}

	setSelected(item: FilterItem): void {
		this._selected[item.id] = { ...item };
	}

	removeSelected(id: number): void {
		delete this._selected[id];
		this._initial = this._initial.map((array: Array<number>) =>
			array.filter((item: number) => item !== id)
		);
	}

	isSelected(): boolean {
		return !!this.getSelected().length;
	}

	clear(): void {
		for (let item in this._selected) delete this._selected[item];
		this._initial = [];
	}
}